import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import ResponsiveDialog from '@/components/ResponsiveDialog';
import { useAuth } from '@/contexts/AuthContext';

interface AuthenticationModalProps {
  openDialog: boolean;
  setOpenDialog: (open: boolean) => void;
}

const STRICT_EMAIL_REGEX = /^[A-Z0-9._%+-]+@(?:[A-Z0-9-]+\.)+[A-Z]{2,}$/i;

const AuthenticationModal = ({
  openDialog,
  setOpenDialog,
}: AuthenticationModalProps) => {
  const { initiateLogin, isLoginLoading } = useAuth();
  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .matches(STRICT_EMAIL_REGEX, 'Invalid email format')
        .required('Email is required'),
    }),
    onSubmit: async values => {
      await initiateLogin(values.email);
    },
  });

  return (
    <ResponsiveDialog
      open={openDialog}
      maxWidth='sm'
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
        '& .MuiDialog-paper': {
          borderRadius: '8px',
        },
      }}
    >
      <DialogTitle sx={{ p: 2 }}>Enter your email address</DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <TextField
          name='email'
          placeholder='Email Address'
          fullWidth
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
      </DialogContent>
      <DialogActions sx={{ gap: 1, p: 2 }}>
        <Button
          variant='outlined'
          onClick={() => {
            setOpenDialog(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          startIcon={isLoginLoading ? <CircularProgress size='1rem' /> : null}
          disabled={isLoginLoading}
          onClick={() => formik.handleSubmit()}
        >
          Submit
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default AuthenticationModal;
